import { useState } from 'react';
import { toast } from 'react-toastify';
import { notificationService } from '../../services/notification.service';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';

export const NotificationsPage = () => {
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'ANNOUNCEMENT',
    recipients: ''
  });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSending(true);
      const recipientList = formData.recipients
        ? formData.recipients.split(',').map(r => r.trim()).filter(r => r)
        : undefined;

      await notificationService.sendNotification({
        title: formData.title,
        message: formData.message,
        type: formData.type,
        recipients: recipientList
      });

      toast.success('Notification sent successfully');
      setFormData({
        title: '',
        message: '',
        type: 'ANNOUNCEMENT',
        recipients: ''
      });
    } catch (error: any) {
      toast.error(error.message || 'Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Notification Center</h1>
        <p className="text-gray-600">Send notifications and announcements</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Send Notification Form */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Send Notification</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                rows={4}
                required
              />
            </div>
            <Select
              label="Type"
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              options={[
                { value: 'ANNOUNCEMENT', label: 'Announcement' },
                { value: 'EMERGENCY', label: 'Emergency' },
                { value: 'REMINDER', label: 'Reminder' },
                { value: 'ALERT', label: 'Alert' }
              ]}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Recipients (optional)
              </label>
              <input
                type="text"
                value={formData.recipients}
                onChange={(e) => setFormData({ ...formData, recipients: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="Member IDs separated by commas (leave empty for all)"
              />
              <p className="text-xs text-gray-500 mt-1">
                Leave empty to send to all members
              </p>
            </div>
            <Button type="submit" disabled={sending} className="w-full">
              {sending ? 'Sending...' : 'Send Notification'}
            </Button>
          </form>
        </div>

        {/* Info Panel */}
        <div className="space-y-6">
          <div className="bg-blue-50 rounded-lg p-6">
            <h3 className="font-semibold text-blue-900 mb-2">📢 Announcement</h3>
            <p className="text-sm text-blue-800">
              General announcements for all members or specific groups
            </p>
          </div>
          <div className="bg-red-50 rounded-lg p-6">
            <h3 className="font-semibold text-red-900 mb-2">🚨 Emergency</h3>
            <p className="text-sm text-red-800">
              Critical alerts that require immediate attention from all members
            </p>
          </div>
          <div className="bg-yellow-50 rounded-lg p-6">
            <h3 className="font-semibold text-yellow-900 mb-2">⏰ Reminder</h3>
            <p className="text-sm text-yellow-800">
              Reminders for appointments, renewals, or pending actions
            </p>
          </div>
          <div className="bg-purple-50 rounded-lg p-6">
            <h3 className="font-semibold text-purple-900 mb-2">⚠️ Alert</h3>
            <p className="text-sm text-purple-800">
              Important updates or warnings for members
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
